import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { ChatLine } from '@gitbook/spine-react';

/**
 * Live, animated indicator shown in the conversation while the agent is processing a response.
 * It mirrors the agent's current activity (thinking, running a tool, writing its answer) so the
 * user always has a clear signal that the agent is working, similar to Claude.
 */
export function AgentWorkingIndicator(props: { activity: api.AgentActivity | null }) {
    const { activity } = props;

    return (
        <ChatLine
            loading
            icon={<Icon.AIAgentAnimated state="loading" aria-hidden="true" role="presentation" />}
        >
            {getActivityLabel(activity)}
        </ChatLine>
    );
}

/**
 * Get the label to display for the agent's current activity.
 */
export function getActivityLabel(activity: api.AgentActivity | null): string {
    if (!activity || activity.type === 'thinking') {
        return 'Thinking…';
    }

    if (activity.type === 'responding') {
        return 'Writing…';
    }

    // A tool that came with its own words for what it is doing — an MCP server's, where the verb
    // below could only ever be the generic one, since every such call is named `mcp`.
    if (activity.label) {
        return `${activity.label}…`;
    }

    const verb = activity.tool
        ? TOOL_ACTIVITY_VERB[activity.tool as api.AIToolCall['tool']]
        : undefined;
    return `${verb ?? 'Working'}…`;
}

/**
 * Maps each tool to the verb describing what the agent is doing while it runs.
 * Typed as a complete record so adding a new tool forces a verb to be defined here.
 */
const TOOL_ACTIVITY_VERB: Record<api.AIToolCall['tool'], string> = {
    startContentImport: 'Importing',
    checkContentImport: 'Reading',
    importOpenAPISpec: 'Importing',
    mergeChangeRequest: 'Merging',
    siteSetupStep: 'Setting up',
    getPageContent: 'Reading',
    getRecordContent: 'Reading',
    getPages: 'Reading',
    getReusableContent: 'Reading',
    getReusableContents: 'Reading',
    getPageDiff: 'Reading',
    listModifiedPages: 'Reading',
    getVariables: 'Reading',
    getTags: 'Reading',
    search: 'Searching',
    editPage: 'Editing',
    deletePage: 'Editing',
    renamePage: 'Editing',
    movePage: 'Editing',
    editReusableContent: 'Editing',
    updateVariables: 'Editing',
    updateTags: 'Editing',
    createPage: 'Creating',
    createReusableContent: 'Creating',
    createChangeRequest: 'Working',
    getChangeRequestDetails: 'Reading',
    setChangeRequestDetails: 'Editing',
    archiveChangeRequest: 'Working',
    comment: 'Commenting',
    replyToComment: 'Commenting',
    getComments: 'Reading',
    submitChangeRequestReview: 'Reviewing',
    startBackgroundAgent: 'Delegating',
    mcp: 'Working',
    other: 'Working',
};
